import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, MapPin, Edit, Trash2, Ruler, DollarSign, Home, Wand2, Loader2, CheckCircle2, Search, Filter, CheckSquare, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import CrearLotesMasivo from '@/components/CrearLotesMasivo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  numero: '',
  manzana: '',
  calle: '',
  descripcion: '',
  superficie: '',
  medidas: '',
  precio: '',
  estado: 'disponible',
  notas: ''
};

export default function FraccionamientoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fraccionamiento, setFraccionamiento] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingLote, setEditingLote] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [formData, setFormData] = useState(initialFormData);
  
  // Nuevos estados para filtros y selección
  const [searchTerm, setSearchTerm] = useState('');
  const [filterManzana, setFilterManzana] = useState('todas');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [selectedLotes, setSelectedLotes] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [fraccRes, lotesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/fraccionamientos/${id}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/lotes?fraccionamiento_id=${id}`, { credentials: 'include' })
      ]);
      
      if (fraccRes.ok) {
        setFraccionamiento(await fraccRes.json());
      }
      if (lotesRes.ok) {
        const lotesData = await lotesRes.json();
        // Ordenar por número
        lotesData.sort((a, b) => {
          const numA = parseInt(a.numero) || 0;
          const numB = parseInt(b.numero) || 0;
          return numA - numB;
        });
        setLotes(lotesData);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Obtener lista única de manzanas
  const manzanasUnicas = [...new Set(lotes.map(l => l.manzana).filter(Boolean))].sort();

  // Filtrar lotes
  const filteredLotes = lotes.filter(lote => {
    // Filtro por búsqueda
    const matchSearch = searchTerm === '' || 
      lote.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lote.manzana?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lote.calle?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro por manzana
    const matchManzana = filterManzana === 'todas' || 
      (filterManzana === 'sin_manzana' ? !lote.manzana : lote.manzana === filterManzana);
    
    // Filtro por estado
    const matchEstado = filterEstado === 'todos' || lote.estado === filterEstado;
    
    return matchSearch && matchManzana && matchEstado;
  });

  const handleOpenCreate = () => {
    setEditingLote(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleOpenEdit = (lote) => {
    setEditingLote(lote);
    setFormData({
      numero: lote.numero || '',
      manzana: lote.manzana || '',
      calle: lote.calle || '',
      descripcion: lote.descripcion || '',
      superficie: lote.superficie?.toString() || '',
      medidas: lote.medidas || '',
      precio: (lote.precio || lote.precio_actual || lote.precio_base)?.toString() || '',
      estado: lote.estado || 'disponible',
      notas: lote.notas || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingLote
        ? `${BACKEND_URL}/api/lotes/${editingLote.lote_id}`
        : `${BACKEND_URL}/api/lotes`;
      
      const payload = {
        fraccionamiento_id: id,
        numero: formData.numero,
        superficie: parseFloat(formData.superficie) || 0,
        precio: parseFloat(formData.precio) || 0,
        estado: formData.estado
      };
      
      if (formData.manzana) payload.manzana = formData.manzana;
      if (formData.calle) payload.calle = formData.calle;
      if (formData.descripcion) payload.descripcion = formData.descripcion;
      if (formData.medidas) payload.medidas = formData.medidas;
      if (formData.notas) payload.notas = formData.notas;
      
      const response = await fetch(url, {
        method: editingLote ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar lote');
      }
      
      toast.success(editingLote ? 'Lote actualizado correctamente' : 'Lote creado correctamente');
      setDialogOpen(false);
      setEditingLote(null);
      setFormData(initialFormData);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Error al guardar lote');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/lotes/${deleteDialog.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Error al eliminar');
      toast.success('Lote eliminado correctamente');
      setDeleteDialog({ open: false, id: null });
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar lote');
    }
  };

  // Funciones de selección múltiple
  const toggleSelectLote = (loteId) => {
    const newSelected = new Set(selectedLotes);
    if (newSelected.has(loteId)) {
      newSelected.delete(loteId);
    } else {
      newSelected.add(loteId);
    }
    setSelectedLotes(newSelected);
  };

  const selectAllVisible = () => {
    const newSelected = new Set(selectedLotes);
    filteredLotes.forEach(lote => newSelected.add(lote.lote_id));
    setSelectedLotes(newSelected);
  };

  const deselectAll = () => {
    setSelectedLotes(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedLotes.size === 0) return;
    
    setBulkDeleting(true);
    let deleted = 0;
    let errors = 0;
    
    for (const loteId of selectedLotes) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/lotes/${loteId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (response.ok) {
          deleted++;
        } else {
          errors++;
        }
      } catch {
        errors++;
      }
    }
    
    setBulkDeleting(false);
    setBulkDeleteDialog(false);
    setSelectedLotes(new Set());
    setSelectionMode(false);
    
    if (deleted > 0) {
      toast.success(`${deleted} lotes eliminados correctamente`);
    }
    if (errors > 0) {
      toast.error(`${errors} lotes no se pudieron eliminar`);
    }
    
    fetchData();
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      disponible: 'bg-emerald-500/20 text-emerald-400',
      apartado: 'bg-amber-500/20 text-amber-400',
      vendido: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      disponible: 'Disponible',
      apartado: 'Apartado',
      vendido: 'Vendido'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[estado] || styles.disponible}`}>
        {labels[estado] || estado}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!fraccionamiento) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <p className="text-zinc-500">Fraccionamiento no encontrado</p>
      </div>
    );
  }

  return (
    <div data-testid="fraccionamiento-detalle" className="min-h-screen bg-zinc-950 p-8">
      <Button 
        variant="outline" 
        onClick={() => navigate('/fraccionamientos')}
        className="mb-6 border-zinc-700 text-white hover:bg-zinc-800"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver
      </Button>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">{fraccionamiento.nombre}</h1>
          <div className="flex items-center gap-2 text-zinc-400 mt-2">
            <MapPin className="w-4 h-4 text-zinc-600" />
            <span>{fraccionamiento.ubicacion}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => setBulkDialogOpen(true)}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Crear Masivo
          </Button>
          <Button 
            onClick={handleOpenCreate}
            data-testid="create-lote-button" 
            className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar Lote
          </Button>
        </div>
      </div>

      {fraccionamiento.plano_url && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
          <img
            src={fraccionamiento.plano_url}
            alt={`Plano de ${fraccionamiento.nombre}`}
            className="w-full h-96 object-cover"
          />
        </div>
      )}

      {/* Barra de filtros y búsqueda */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Búsqueda */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="search-lotes"
              placeholder="Buscar por número, manzana..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          {/* Filtro por manzana */}
          <div className="min-w-[150px]">
            <Select value={filterManzana} onValueChange={setFilterManzana}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                <Filter className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="Manzana" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="todas" className="text-white">Todas las manzanas</SelectItem>
                <SelectItem value="sin_manzana" className="text-white">Sin manzana</SelectItem>
                {manzanasUnicas.map(mz => (
                  <SelectItem key={mz} value={mz} className="text-white">Manzana {mz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Filtro por estado */}
          <div className="min-w-[150px]">
            <Select value={filterEstado} onValueChange={setFilterEstado}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="todos" className="text-white">Todos los estados</SelectItem>
                <SelectItem value="disponible" className="text-white">Disponible</SelectItem>
                <SelectItem value="apartado" className="text-white">Apartado</SelectItem>
                <SelectItem value="vendido" className="text-white">Vendido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Botón modo selección */}
          <Button
            variant={selectionMode ? "default" : "outline"}
            onClick={() => {
              setSelectionMode(!selectionMode);
              if (selectionMode) {
                setSelectedLotes(new Set());
              }
            }}
            className={selectionMode 
              ? "bg-amber-500 hover:bg-amber-400 text-black" 
              : "border-zinc-700 text-white hover:bg-zinc-800"}
          >
            <CheckSquare className="w-4 h-4 mr-2" />
            {selectionMode ? 'Cancelar' : 'Seleccionar'}
          </Button>
        </div>
        
        {/* Barra de acciones de selección */}
        {selectionMode && (
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-800">
            <span className="text-sm text-zinc-400">
              {selectedLotes.size} lotes seleccionados
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={selectAllVisible}
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              Seleccionar todos ({filteredLotes.length})
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={deselectAll}
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              Deseleccionar
            </Button>
            {selectedLotes.size > 0 && (
              <Button
                size="sm"
                onClick={() => setBulkDeleteDialog(true)}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar seleccionados
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Contador de resultados */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">
          Lotes ({filteredLotes.length}{filteredLotes.length !== lotes.length && ` de ${lotes.length}`})
        </h2>
        {(searchTerm || filterManzana !== 'todas' || filterEstado !== 'todos') && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setSearchTerm('');
              setFilterManzana('todas');
              setFilterEstado('todos');
            }}
            className="text-zinc-400 hover:text-white"
          >
            <X className="w-4 h-4 mr-1" />
            Limpiar filtros
          </Button>
        )}
      </div>

      {filteredLotes.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-16 text-center">
          <MapPin className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
          <h3 className="text-lg font-bold text-white mb-2">
            {lotes.length === 0 ? 'No hay lotes' : 'No se encontraron lotes'}
          </h3>
          <p className="text-zinc-500 mb-4">
            {lotes.length === 0 
              ? 'Agrega el primer lote a este fraccionamiento' 
              : 'Intenta con otros filtros de búsqueda'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredLotes.map((lote) => (
            <div 
              key={lote.lote_id} 
              data-testid={`lote-card-${lote.lote_id}`} 
              onClick={() => selectionMode && toggleSelectLote(lote.lote_id)}
              className={`bg-zinc-900 border rounded-xl p-5 transition-all cursor-pointer ${
                selectionMode 
                  ? selectedLotes.has(lote.lote_id)
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-zinc-800 hover:border-zinc-600'
                  : 'border-zinc-800 hover:border-amber-500/50'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {selectionMode && (
                    <div className="text-amber-500">
                      {selectedLotes.has(lote.lote_id) 
                        ? <CheckSquare className="w-5 h-5" /> 
                        : <Square className="w-5 h-5" />}
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-white">Lote {lote.numero}</h3>
                    {lote.manzana && (
                      <span className="text-xs text-zinc-500 flex items-center gap-1">
                        <Home className="w-3 h-3" /> Manzana {lote.manzana}
                      </span>
                    )}
                  </div>
                </div>
                {getEstadoBadge(lote.estado)}
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <Ruler className="w-4 h-4 text-zinc-600" />
                  <span>{lote.superficie || 0} m²</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-amber-500" />
                  <span className="font-mono font-bold text-amber-400">${(lote.precio || lote.precio_actual || 0).toLocaleString('es-MX')}</span>
                </div>
              </div>
              
              {!selectionMode && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    onClick={(e) => { e.stopPropagation(); handleOpenEdit(lote); }}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, id: lote.lote_id }); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog Crear/Editar Lote */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editingLote ? 'Editar Lote' : 'Nuevo Lote'}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {editingLote ? 'Modifica la información del lote' : 'Agrega un nuevo lote al fraccionamiento'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Número de Lote *</Label>
                <Input
                  data-testid="input-numero"
                  value={formData.numero}
                  onChange={(e) => setFormData({...formData, numero: e.target.value})}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Manzana</Label>
                <Input
                  data-testid="input-manzana"
                  value={formData.manzana}
                  onChange={(e) => setFormData({...formData, manzana: e.target.value})}
                  placeholder="Ej: A"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Superficie (m²) *</Label>
                <Input
                  data-testid="input-superficie"
                  type="number"
                  step="0.01"
                  value={formData.superficie}
                  onChange={(e) => setFormData({...formData, superficie: e.target.value})}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Medidas</Label>
                <Input
                  data-testid="input-medidas"
                  value={formData.medidas}
                  onChange={(e) => setFormData({...formData, medidas: e.target.value})}
                  placeholder="10x12"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300">Precio (USD) *</Label>
              <Input
                data-testid="input-precio"
                type="number"
                step="0.01"
                value={formData.precio}
                onChange={(e) => setFormData({...formData, precio: e.target.value})}
                required
                placeholder="Ej: 100000"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Estado</Label>
              <Select value={formData.estado} onValueChange={(v) => setFormData({...formData, estado: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="disponible" className="text-white">Disponible</SelectItem>
                  <SelectItem value="apartado" className="text-white">Apartado</SelectItem>
                  <SelectItem value="vendido" className="text-white">Vendido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Notas</Label>
              <Textarea
                data-testid="input-notas"
                value={formData.notas}
                onChange={(e) => setFormData({...formData, notas: e.target.value})}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">
              {editingLote ? 'Guardar Cambios' : 'Crear Lote'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Componente reutilizable para crear lotes masivamente */}
      <CrearLotesMasivo
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        fraccionamiento={fraccionamiento}
        onSuccess={fetchData}
      />

      {/* AlertDialog Eliminar Individual */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar este lote?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog Eliminar Múltiples */}
      <AlertDialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar {selectedLotes.size} lotes?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta acción eliminará permanentemente todos los lotes seleccionados. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              disabled={bulkDeleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Eliminar {selectedLotes.size} lotes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
